import { useCallback, useEffect, useId, useState } from "react";
import { Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  ItemArtworkSupply,
  RepeatableItemArtworkView,
  StaffProof,
} from "@shared-core/data/artwork-staff-types";
import type {
  ItemArtworkSupplyForm,
  OldRevisionStock,
  RepeatableItemArtworkCommand,
} from "@shared-core/data/repeatable-item-artwork-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import {
  hasCurrentRevision,
  itemArtworkTone,
  proofsAwaitingApproval,
  type ItemArtworkTone,
} from "@shared-core/domain/repeatable-item-artwork";
import { paths } from "@shared-core/routing/routes";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../primitives/card";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import { RadioGroup, RadioGroupItem } from "../../primitives/radio-group";
import { Textarea } from "../../primitives/textarea";

/**
 * The item artwork panel on a repeatable catalogue item's staff page
 * (brandhub-order-routing 13 §5.1–§5.3; order to invoice doc 83 §6 Q3): where
 * its standing artwork stands, a new revision's proof to approve for the
 * customer, confirming the current revision's valid until, and supplying a
 * new revision.
 *
 * Reads views' `/views/repeatable-item-artwork/{id}` (AW3c-3); every sentence
 * about the artwork is the read's, and so is every write path: the supply form
 * is read at `artwork_parts_url`, files checked at `check_url`, supply and
 * confirm posted at `supply_url` / `confirm_validity_url`. An action whose URL
 * is null is not offered. Field refusals bind to their inputs.
 *
 * `onChange` is called after each successful supply, confirm or proof approval, once the
 * panel's own re-read has finished, so a page showing the same artwork can
 * refetch.
 */
export function ItemArtworkPanel({
  catalogueItemId,
  onChange,
}: {
  catalogueItemId: string;
  onChange?: () => void;
}) {
  const adapter = useDataAdapter();
  const [view, setView] = useState<RepeatableItemArtworkView | null>(null);
  const [form, setForm] = useState<ItemArtworkSupplyForm | null>(null);
  const [state, setState] = useState<
    "loading" | "ready" | "missing" | "failed"
  >("loading");

  const load = useCallback(() => {
    setState("loading");
    adapter
      .getRepeatableItemArtwork(catalogueItemId)
      .then(async (read) => {
        // The parts are read where the view says; no URL, no supply form.
        const supplyForm = read?.artwork_parts_url
          ? await adapter.getRepeatableItemSupplyForm(read.artwork_parts_url)
          : null;
        setView(read);
        setForm(supplyForm);
        setState(read ? "ready" : "missing");
      })
      .catch(() => setState("failed"));
  }, [adapter, catalogueItemId]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = useCallback(
    async (command: RepeatableItemArtworkCommand) => {
      if (!view) return;
      const reread = await adapter.applyRepeatableItemArtworkCommand(
        view,
        command,
      );
      setView(reread);
      if (!reread) setState("missing");
      onChange?.();
    },
    [adapter, view, onChange],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Item artwork</CardTitle>
      </CardHeader>
      <CardContent>
        {state === "failed" ? (
          <p role="alert" className="text-sm text-destructive">
            Couldn&rsquo;t load this item&rsquo;s artwork.{" "}
            <button
              type="button"
              onClick={load}
              className="font-medium underline underline-offset-2"
            >
              Try again
            </button>
          </p>
        ) : state === "missing" ? (
          <p className="text-sm text-muted-foreground">
            This item&rsquo;s artwork isn&rsquo;t shown here: it is not a
            repeatable item of a customer in your portfolio.
          </p>
        ) : state === "loading" || !view ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <ItemArtworkContent view={view} form={form} onCommand={apply} />
        )}
      </CardContent>
    </Card>
  );
}

const TONE_LABEL: Record<ItemArtworkTone, string> = {
  neutral: "No artwork",
  success: "In use",
  warning: "Needs attention",
  error: "Orders stop",
};

type OpenForm = "none" | "supply" | "confirm";

function ItemArtworkContent({
  view,
  form,
  onCommand,
}: {
  view: RepeatableItemArtworkView;
  form: ItemArtworkSupplyForm | null;
  onCommand: (command: RepeatableItemArtworkCommand) => Promise<void>;
}) {
  const tone = itemArtworkTone(view);
  const current = hasCurrentRevision(view);
  // Each action is offered only where the read carries its URL.
  const canConfirm = current && view.confirm_validity_url !== null;
  const canSupply = view.supply_url !== null && form !== null;
  const [openForm, setOpenForm] = useState<OpenForm>(
    view.validity.status === "none" && canSupply ? "supply" : "none",
  );
  const close = () => setOpenForm("none");

  return (
    <div className="space-y-6">
      <section aria-label="Current artwork" className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={tone}>{TONE_LABEL[tone]}</Badge>
          <p className="text-sm font-medium">{view.validity.sentence}</p>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          {canConfirm && (
            <Button
              variant={openForm === "confirm" ? "secondary" : "outline"}
              size="sm"
              onClick={() =>
                setOpenForm(openForm === "confirm" ? "none" : "confirm")
              }
            >
              Confirm still valid
            </Button>
          )}
          {canSupply && (
            <Button
              variant={openForm === "supply" ? "secondary" : "outline"}
              size="sm"
              onClick={() =>
                setOpenForm(openForm === "supply" ? "none" : "supply")
              }
            >
              {view.revisions.length === 0
                ? "Supply artwork"
                : "Supply new revision"}
            </Button>
          )}
        </div>
      </section>

      {openForm === "confirm" &&
        canConfirm &&
        view.validity.revision_no !== null && (
          <ConfirmValidityForm
            revisionNo={view.validity.revision_no}
            onCommand={onCommand}
            onDone={close}
          />
        )}
      {openForm === "supply" && canSupply && (
        <SupplyForm
          view={view}
          form={form}
          asksOldStock={current}
          onCommand={onCommand}
          onDone={close}
        />
      )}

      {view.held_orders.length > 0 && (
        <section
          aria-label="Orders stopped on this artwork"
          className="space-y-2"
        >
          <h4 className="text-sm font-medium">
            Orders stopped on this artwork ({view.held_orders.length})
          </h4>
          <ul className="space-y-1 text-sm">
            {view.held_orders.map((held) => (
              <li key={held.line_ref}>
                <Link
                  to={paths.orderDetail({ orderNo: held.order_no })}
                  className="font-medium underline underline-offset-2"
                >
                  {held.line_ref}
                </Link>{" "}
                <span className="text-muted-foreground">
                  {held.stop_reason}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {proofsAwaitingApproval(view).map((proof) => (
        <ProofToApprove key={proof.id} proof={proof} onCommand={onCommand} />
      ))}

      {view.supplies.length > 0 && (
        <section aria-label="Supply history" className="space-y-2">
          <h4 className="text-sm font-medium">Supply history</h4>
          <ul className="divide-y rounded-md border text-sm">
            {view.supplies.map((supply) => (
              <SupplyHistoryEntry key={supply.revision_id} supply={supply} />
            ))}
          </ul>
        </section>
      )}

      {view.revisions.length > 0 && (
        <section aria-label="Revisions" className="space-y-2">
          <h4 className="text-sm font-medium">Revisions</h4>
          <ul className="divide-y rounded-md border text-sm">
            {view.revisions.map((revision) => (
              <li
                key={revision.revision_no}
                className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
              >
                <span className="font-medium">{revision.label}</span>
                <span className="text-muted-foreground">
                  {revision.status_text}
                </span>
                <span>
                  {revision.valid_until_label ??
                    (view.valid_until_required
                      ? "Needs a valid-until date"
                      : "No expiry")}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** One supply, worded by the read: who and when are in its sentence, the old stock in its stock text. */
function SupplyHistoryEntry({ supply }: { supply: ItemArtworkSupply }) {
  return (
    <li className="space-y-0.5 px-3 py-2">
      <p>
        <span className="font-medium">{supply.label}</span>{" "}
        <span className="text-muted-foreground">{supply.sentence}</span>
      </p>
      {supply.file_names.length > 0 && (
        <p className="text-muted-foreground">{supply.file_names.join(", ")}</p>
      )}
      {supply.valid_until && (
        <p>Valid until {formatDateOrDash(supply.valid_until)}</p>
      )}
      {supply.stock_text && <p>{supply.stock_text}</p>}
    </li>
  );
}

function ProofToApprove({
  proof,
  onCommand,
}: {
  proof: StaffProof;
  onCommand: (command: RepeatableItemArtworkCommand) => Promise<void>;
}) {
  const [note, setNote] = useState("");
  const [noteError, setNoteError] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const noteId = useId();

  const approve = async () => {
    setFailure(null);
    if (!note.trim()) {
      setNoteError("Say how the customer approved it");
      return;
    }
    setNoteError(null);
    setBusy(true);
    try {
      await onCommand({
        kind: "approve_proof",
        proofId: proof.id,
        input: {
          outcome: "approved",
          wording_version: proof.wording_version,
          accepted_finding_ids: proof.findings
            .filter((f) => f.tier === "worth_knowing")
            .map((f) => f.id),
          note: note.trim(),
        },
      });
    } catch {
      setFailure("The approval wasn't saved. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-label={`Rev ${proof.of_revision}`}
      className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/30"
    >
      <p className="text-sm font-medium">
        Rev {proof.of_revision}: {proof.status_line}
      </p>
      <p className="text-sm text-muted-foreground">
        The current revision stays the one orders use until this one is
        approved.
      </p>
      <p className="text-sm">{proof.label} is ready to check.</p>
      <div className="space-y-1">
        <Label htmlFor={noteId}>How the customer approved it</Label>
        <Textarea
          id={noteId}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          aria-invalid={noteError ? true : undefined}
          aria-describedby={noteError ? `${noteId}-error` : undefined}
          rows={2}
        />
        {noteError && (
          <p id={`${noteId}-error`} className="text-sm text-red-600">
            {noteError}
          </p>
        )}
      </div>
      {failure && (
        <p role="alert" className="text-sm text-red-600">
          {failure}
        </p>
      )}
      <Button size="sm" onClick={approve} disabled={busy}>
        Approve proof for the customer
      </Button>
    </section>
  );
}

function useFieldErrors() {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState<string | null>(null);
  const fail = (err: unknown) => {
    const fieldErrors = extractFieldErrors(err);
    if (fieldErrors.length === 0) {
      setErrors({});
      setSummary("That wasn't saved. Try again.");
      return;
    }
    setErrors(Object.fromEntries(fieldErrors.map((e) => [e.field, e.message])));
    setSummary(
      `${fieldErrors.length} ${fieldErrors.length === 1 ? "problem" : "problems"} to fix.`,
    );
  };
  const reset = () => {
    setErrors({});
    setSummary(null);
  };
  return { errors, summary, fail, reset };
}

function FieldMessage({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p id={id} className="text-sm text-red-600">
      {message}
    </p>
  );
}

function ConfirmValidityForm({
  revisionNo,
  onCommand,
  onDone,
}: {
  revisionNo: number;
  onCommand: (command: RepeatableItemArtworkCommand) => Promise<void>;
  onDone: () => void;
}) {
  const [validUntil, setValidUntil] = useState("");
  const [busy, setBusy] = useState(false);
  const { errors, summary, fail, reset } = useFieldErrors();
  const id = useId();
  const dateError = errors.valid_until ?? errors.revision_no;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    reset();
    setBusy(true);
    try {
      await onCommand({ kind: "confirm_validity", revisionNo, validUntil });
      onDone();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      aria-label="Confirm still valid"
      className="space-y-3 rounded-md border p-3"
    >
      <p className="text-sm">
        Confirm Rev {revisionNo} is still the right artwork, with its new last
        valid day.
      </p>
      {summary && (
        <p role="alert" className="text-sm font-medium text-destructive">
          {summary}
        </p>
      )}
      <div className="space-y-1">
        <Label htmlFor={`${id}-date`}>Valid until</Label>
        <Input
          id={`${id}-date`}
          type="date"
          required
          value={validUntil}
          onChange={(e) => setValidUntil(e.target.value)}
          aria-invalid={dateError ? true : undefined}
          aria-describedby={dateError ? `${id}-date-error` : undefined}
          className="w-48"
        />
        <FieldMessage id={`${id}-date-error`} message={dateError} />
      </div>
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={busy || !validUntil}>
          Confirm
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function SupplyForm({
  view,
  form,
  asksOldStock,
  onCommand,
  onDone,
}: {
  view: RepeatableItemArtworkView;
  form: ItemArtworkSupplyForm;
  asksOldStock: boolean;
  onCommand: (command: RepeatableItemArtworkCommand) => Promise<void>;
  onDone: () => void;
}) {
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [validUntil, setValidUntil] = useState("");
  const [oldRevisionStock, setOldRevisionStock] = useState<
    OldRevisionStock | ""
  >("");
  const [busy, setBusy] = useState(false);
  const { errors, summary, fail, reset } = useFieldErrors();
  const id = useId();
  const parts = form.parts;
  // Without a check URL no new file can be checked, so none can be supplied.
  const canUpload = view.check_url !== null;
  const fileError = (partKey: string) =>
    errors[`artwork_checks.${partKey}`] ??
    (parts.length === 1 ? errors.artwork_checks : undefined);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    reset();
    setBusy(true);
    try {
      await onCommand({
        kind: "supply",
        customerId: form.customer_id,
        files: parts.flatMap((part) => {
          const file = files[part.part_key];
          return file ? [{ partKey: part.part_key, file }] : [];
        }),
        validUntil: validUntil || null,
        oldRevisionStock:
          asksOldStock && oldRevisionStock ? oldRevisionStock : null,
      });
      onDone();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      aria-label="Supply artwork"
      className="space-y-4 rounded-md border p-3"
    >
      {!canUpload ? (
        <>
          <p role="status" className="text-sm">
            You can&rsquo;t upload a new file here, so a new revision
            can&rsquo;t be supplied from this page.
          </p>
          <Button type="button" size="sm" variant="ghost" onClick={onDone}>
            Close
          </Button>
        </>
      ) : (
        <>
          <p className="text-sm">
            Each file is checked for {view.customer_name}, then corrected and
            proofed; approve the proof here for the customer.
          </p>
          {summary && (
            <p role="alert" className="text-sm font-medium text-destructive">
              {summary}
            </p>
          )}
          {parts.map((part) => {
            const inputId = `${id}-file-${part.part_key}`;
            const message = fileError(part.part_key);
            return (
              <div key={part.part_key} className="space-y-1">
                <Label htmlFor={inputId}>
                  {parts.length === 1
                    ? "Artwork file"
                    : `${part.part_label} file`}
                </Label>
                <Input
                  id={inputId}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(e) =>
                    setFiles((prev) => ({
                      ...prev,
                      [part.part_key]: e.target.files?.[0] ?? null,
                    }))
                  }
                  aria-invalid={message ? true : undefined}
                  aria-describedby={message ? `${inputId}-error` : undefined}
                />
                <FieldMessage id={`${inputId}-error`} message={message} />
              </div>
            );
          })}
          <div className="space-y-1">
            <Label htmlFor={`${id}-valid-until`}>
              Valid until{view.valid_until_required ? "" : " (optional)"}
            </Label>
            <Input
              id={`${id}-valid-until`}
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              aria-required={view.valid_until_required}
              aria-invalid={errors.valid_until ? true : undefined}
              aria-describedby={
                errors.valid_until ? `${id}-valid-until-error` : undefined
              }
              className="w-48"
            />
            {view.valid_until_required && !errors.valid_until && (
              <p className="text-xs text-muted-foreground">
                This item&rsquo;s artwork expires: enter the last day it is
                valid.
              </p>
            )}
            <FieldMessage
              id={`${id}-valid-until-error`}
              message={errors.valid_until}
            />
          </div>
          {asksOldStock && (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">
                Stock printed from Rev {view.validity.revision_no}
              </legend>
              <RadioGroup
                value={oldRevisionStock}
                onValueChange={(value) =>
                  setOldRevisionStock(value as OldRevisionStock)
                }
                aria-invalid={errors.old_revision_stock ? true : undefined}
                aria-describedby={
                  errors.old_revision_stock
                    ? `${id}-old-stock-error`
                    : undefined
                }
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="use_up_first" id={`${id}-use-up`} />
                  <Label htmlFor={`${id}-use-up`} className="font-normal">
                    Use it up first (the change is cosmetic)
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="withdraw" id={`${id}-withdraw`} />
                  <Label htmlFor={`${id}-withdraw`} className="font-normal">
                    Withdraw it once the new revision is current
                  </Label>
                </div>
              </RadioGroup>
              <FieldMessage
                id={`${id}-old-stock-error`}
                message={errors.old_revision_stock}
              />
            </fieldset>
          )}
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={busy}>
              Supply
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={onDone}>
              Cancel
            </Button>
          </div>
        </>
      )}
    </form>
  );
}
